// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the story Khipus in the browser (Khipu plan, slice 3):
 * which catalogue datasets have one (`/api/stories`, fetched once a page)
 * and the Khipu itself (`/api/stories/:slug`). An `opening` is how a
 * dataset opens (its camera, date, speed and borders, put on quietly); a
 * `story` plays in the Khipu player. They are NOAA's guided sequences, converted
 * (`scripts/convert-noaa-stories.ts`).
 */
import type { Khipu } from './khipu'

export interface StoryEntry {
  slug: string
  role: 'opening' | 'story'
  steps: number
  title: string
}

let indexPromise: Promise<Record<string, StoryEntry>> | null = null
const khipus = new Map<string, Promise<Khipu | null>>()

/** Which catalogue datasets have a story Khipu; empty when the route cannot be read. */
export function fetchStoryIndex(fetchImpl: typeof fetch = fetch): Promise<Record<string, StoryEntry>> {
  indexPromise ??= (async () => {
    try {
      const res = await fetchImpl('/api/stories', { headers: { Accept: 'application/json' } })
      if (!res.ok) return {}
      const body = (await res.json()) as { stories?: Record<string, StoryEntry> }
      return body.stories && typeof body.stories === 'object' ? body.stories : {}
    } catch {
      return {}
    }
  })()
  return indexPromise
}

/**
 * The story Khipu for a catalogue dataset, if it has one. AYNI (own catalogue,
 * 2026-10-07): the index is keyed by NOAA's old catalogue ids, so a NOAA
 * library row is also looked up by its `legacyIds`.
 */
export async function storyFor(datasetId: string, fetchImpl: typeof fetch = fetch, legacyIds: readonly string[] = []): Promise<StoryEntry | null> {
  const index = await fetchStoryIndex(fetchImpl)
  const key = [datasetId, ...legacyIds].find((id) => Object.prototype.hasOwnProperty.call(index, id))
  return key ? index[key] : null
}

/** One story Khipu by its slug, kept for the page's life; null when it cannot be read. */
export function fetchStory(slug: string, fetchImpl: typeof fetch = fetch): Promise<Khipu | null> {
  let k = khipus.get(slug)
  if (!k) {
    k = (async () => {
      try {
        const res = await fetchImpl(`/api/stories/${encodeURIComponent(slug)}`, { headers: { Accept: 'application/json' } })
        if (!res.ok) return null
        return ((await res.json()) as { khipu: Khipu }).khipu
      } catch {
        return null
      }
    })()
    khipus.set(slug, k)
  }
  return k
}

/** Forget what was fetched (tests). */
export function forgetStories(): void {
  indexPromise = null
  khipus.clear()
}
